import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Lightbulb, TrendingUp } from 'lucide-react';
import { ROUTES } from '@/router/routes';

const MotionBox = motion(Box);

const CARDS = [
  {
    Icon: Lightbulb,
    eyebrow: 'Why SkolarMinds',
    title: 'Built for Adoption.\nDesigned for Impact.',
    body: 'Discover what makes SkolarMinds different — purpose-built for people, backed by unwavering support until it becomes second nature.',
    cta: 'Explore',
    route: ROUTES.PUBLIC.WHY,
    border: 'rgba(99,102,241,0.28)',
    hoverBorder: 'rgba(99,102,241,0.6)',
    bg: 'linear-gradient(135deg, rgba(99,102,241,0.10) 0%, rgba(124,99,216,0.06) 100%)',
    glow: '0 24px 64px rgba(99,102,241,0.14)',
    iconColor: '#A78BFA',
    iconBg: 'rgba(99,102,241,0.12)',
  },
  {
    Icon: TrendingUp,
    eyebrow: 'The Shift',
    title: 'Real Institutions.\nReal Results.',
    body: 'See how schools and colleges transformed their operations — in their own words, with numbers that speak for themselves.',
    cta: 'See the proof',
    route: ROUTES.PUBLIC.TRANSFORMATION,
    border: 'rgba(16,185,129,0.22)',
    hoverBorder: 'rgba(16,185,129,0.5)',
    bg: 'linear-gradient(135deg, rgba(16,185,129,0.09) 0%, rgba(6,148,162,0.06) 100%)',
    glow: '0 24px 64px rgba(16,185,129,0.10)',
    iconColor: '#34D399',
    iconBg: 'rgba(16,185,129,0.12)',
  },
];

export default function ExploreSection() {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#0A0E17', py: { xs: '64px', md: '80px' }, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <Container maxWidth="lg">

        {/* Heading */}
        <MotionBox
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          sx={{ textAlign: 'center', mb: { xs: '48px', md: '56px' } }}
        >
          <Typography sx={{
            fontSize: '11px', fontWeight: 700, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: '#7C63D8', mb: '12px',
          }}>
            Dig Deeper
          </Typography>
          <Typography sx={{
            fontFamily: '"Syne", sans-serif', fontWeight: 800,
            fontSize: { xs: '26px', sm: '32px', md: '38px' },
            color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.15,
          }}>
            There&apos;s more to the story
          </Typography>
        </MotionBox>

        {/* Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: '20px', md: '28px' } }}>
          {CARDS.map((card, i) => {
            const { Icon } = card;
            return (
              <MotionBox
                key={card.eyebrow}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.52, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => navigate(card.route)}
                sx={{
                  p: { xs: '28px', md: '40px' },
                  borderRadius: '22px',
                  background: card.bg,
                  border: `1px solid ${card.border}`,
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.28s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: card.glow,
                    borderColor: card.hoverBorder,
                  },
                  '&:hover .explore-arrow': {
                    transform: 'translateX(4px)',
                  },
                }}
              >
                {/* Icon + Eyebrow row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', mb: '22px' }}>
                  <Box sx={{
                    width: 50, height: 50, borderRadius: '14px',
                    bgcolor: card.iconBg, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={22} color={card.iconColor} strokeWidth={1.7} />
                  </Box>
                  <Typography sx={{
                    fontSize: '15px', fontWeight: 700, letterSpacing: '0.04em',
                    textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)',
                  }}>
                    {card.eyebrow}
                  </Typography>
                </Box>

                {/* Title */}
                <Typography sx={{
                  fontFamily: '"Syne", sans-serif', fontWeight: 800,
                  fontSize: { xs: '22px', md: '26px' },
                  color: '#fff', lineHeight: 1.22,
                  mb: '16px', letterSpacing: '-0.01em',
                  whiteSpace: 'pre-line',
                }}>
                  {card.title}
                </Typography>

                {/* Body */}
                <Typography sx={{
                  fontSize: '14px', color: 'rgba(255,255,255,0.5)',
                  lineHeight: 1.75, mb: '30px',
                }}>
                  {card.body}
                </Typography>

                {/* CTA */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                  <Typography sx={{
                    fontSize: '14px', fontWeight: 600,
                    color: card.iconColor,
                    fontFamily: '"DM Sans", sans-serif',
                  }}>
                    {card.cta}
                  </Typography>
                  <Box className="explore-arrow" sx={{ color: card.iconColor, display: 'flex', transition: 'transform 0.22s ease' }}>
                    <ArrowRight size={16} />
                  </Box>
                </Box>
              </MotionBox>
            );
          })}
        </Box>

      </Container>
    </Box>
  );
}
